import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useDispatch, useSelector } from 'react-redux';

import { RedirectUrls } from '@/enums/globals';
import { hooks as stockHooks } from '@/domains/esim/stock';
import {
  hooks as orderHooks,
  actions as orderActions,
} from '@/domains/esim/order';
import { hooks as productHooks } from '@/domains/esim/product';
import { customerPrefixes, serialPrefixes } from '@/constants/base';
import GoBack from '@/ui/GoBack/GoBack';
import {
  CardBlock,
  SectionTitle,
  StepsBannerWithTabsVolte,
  Tab,
  TabContent,
  TabContentItem,
  TabPanel,
  Tabs,
  ErrorModal,
  Loader,
  Terms,
} from '@/ui/index';

import { InfoCircleRed } from '@/icons/index';
import CompatibleDevicesModalEsim from '@/ui/CompatibleDevicesModal/CompatibleDevicesModal';
import { EModalType } from '@/enums/modal';
import { cardDatas, salesStepLabels, swapStepSales } from '@/constants/esim';
import { scrollToTop } from '@/utils/scrollToTop';
import { useEsimFlow } from '@/hooks/useEsimFlow';
import { processDeviceData } from '@/utils/filterDevicesData';

import NotInList from '../Modals/NotInList/NotInList';
import PinModal from '../Modals/Pin/PinModal';
import RecognationInfoModal from '../Modals/FaceRecognationInfo/FaceRecognationInfo';
import SwapInformation from '../Modals/SwapInformation/SwapInformation';
import SwapError from '../Modals/SwapError/SwapError';
import OtpError from '../Modals/OtpError/OtpError';
import NotCustomer from '../Modals/NotCustomer/NotCustomer';
import Sima from '../../common/RecordModal/Sima';
import InfoCard from './InfoCard/InfoCard';
import SalesError from '../Modals/SalesError/SalesError';
import EsimTariffs from './Tariffs/Tariff';
import PersonalInfo from './PersonalInfo/PersonalInfo';
import OtpEsim from '../Modals/OTP/OtpEsim';

import styles from './Checkout.module.scss';

const Checkout = ({ esimBannerData }) => {
  const { t, lang } = useTranslation();
  const { activeSima, selectedPlan, disabled, tariffPrice } = useSelector(
    (state) => state.order,
  );
  const dispatch = useDispatch();
  const router = useRouter();
  const id = router?.query?.id;
  const { isSwapFlow } = useEsimFlow();
  const cardsData = cardDatas(t);
  const result = esimBannerData?.filter((obj) => {
    return obj.type === 'volte_banner';
  });
  const FormSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .min(7, t('esim:checkout.inputs.lengthPhone'))
      .required(t('esim:common.required')),
    phoneField: Yup.string().required(t('esim:common.required')),
    countyCodeValue: Yup.string().required(t('esim:common.required')),
    pin: Yup.string().required(t('esim:common.required')),
    captcha: Yup.string().required(t('esim:common.required')),
    terms: Yup.string().required(t('esim:common.required')),
  });

  const FormSchemaForOTP = Yup.object().shape({
    code: Yup.array().of(Yup.string().required()),
  });

  const [activeTab, setActiveTab] = React.useState(0);
  const [isModalActive, setIsModalActive] = React.useState(false);
  const [trackId, setTrackId] = React.useState(null);
  const [termsModal, setTermsModal] = React.useState(false);
  const [notInListModal, setNotInListModal] = React.useState(false);
  const [pinModal, setPinModal] = React.useState(false);
  const [deviceModal, setDeviceModal] = React.useState(false);
  const [deviceItem, setDeviceItem] = React.useState('');
  const [isError, setError] = React.useState(false);
  const [errorText, setErrorText] = React.useState(t('esim:common.errorTitle'));
  const [recognationInfo, setRecognationInfo] = React.useState(false);
  const [swapError, setSwapError] = React.useState(false);
  const [otpAttempt, setOtpAttempt] = React.useState(false);
  const [wrongOtp, setWrongOtp] = React.useState(false);
  const [expired, setExpired] = React.useState(false);
  const [accept, setAccept] = React.useState(true);
  const [notCustomer, setNotCustomer] = React.useState(false);
  const interval = 120000;
  const [timer, setTimer] = React.useState();
  const [finish, setFinish] = React.useState(false);
  const [salesError, setSalesError] = React.useState(false);
  const [reserved, setReserved] = React.useState(false);
  const [errorTextSima, setErrorTextSima] = React.useState('');
  const [modalProps, setModalProps] = React.useState({
    isOpen: false,
    type: null,
  });
  const closeModal = () => {
    setModalProps({ isOpen: false, type: null });
  };
  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    const sessionAccept = sessionStorage.getItem('accept');
    if (sessionAccept) {
      setAccept(false);
    }
  }, []);

  useEffect(() => {
    dispatch(orderActions.setDisabled(activeTab === 1 && !selectedPlan));
  }, [activeTab, selectedPlan]);

  const onChange = (value) => {
    formikMain.setFieldValue('captcha', value);
  };

  const getDeviceItem = (item) => {
    setDeviceItem(item);
    formikMain.setFieldValue('phoneField', item);
    setDeviceModal(!deviceModal);
  };
  const { data: contractData } = orderHooks.useGetCustomerContract({
    lang,
  });
  const { data: numberDetail } = stockHooks.useGetNumberDetails(id, {
    skip: isSwapFlow,
  });

  const totalAmount = () => {
    const numberPrice = Number(numberDetail?.data?.price) || 0;
    const additionalCharge = isSpecialMsisdn ? 4 : 0;
    const planCharge = selectedPlan ? Number(tariffPrice) || 0 : 0;

    return numberPrice + additionalCharge + planCharge;
  };

  const [checkMutation, { isLoading: checkLoading }] =
    orderHooks.useCheckCustomer();
  const [verifyMutation, { isLoading }] = orderHooks.useVerifyCustomer({
    fixedCacheKey: 'Order',
  });
  const [swapMutation, { isLoading: swapLoading }] = orderHooks.useSwap();
  const [simaMutation, { isLoading: simaLoading }] = orderHooks.useSima();
  const { data: tariffData } = productHooks.useGetProducts(lang);
  const { data: deviceData } = orderHooks.useGetDeviceList('');

  const fullDevices = processDeviceData(deviceData, t);

  const [paymentTrigger] = orderHooks.usePaymentRequest();

  const isSpecialMsisdn = numberDetail?.data?.msisdn?.startsWith('1');

  if (typeof window !== 'undefined') {
    localStorage.setItem(
      'tel',
      `0${numberDetail?.data?.prefix}${numberDetail?.data?.msisdn}`,
    );
  }

  const formikMain = useFormik({
    initialValues: {
      phoneField: deviceItem,
      phoneNumberCode: customerPrefixes[0],
      phoneNumber: '',
      countyCode: serialPrefixes[1],
      countyCodeValue: '',
      pin: '',
      terms: false,
    },
    validationSchema: FormSchema,
    async onSubmit(values) {
      const checkUserData = {
        msisdnId: isSwapFlow ? null : router?.query?.id,
        deviceId: deviceItem,
        msisdn: '',
        pinCode: values?.pin,
        serialNumber: values?.countyCode?.name + values?.countyCodeValue,
        contactNumber: values?.phoneNumberCode?.name + values?.phoneNumber,
        transactionType: isSwapFlow
          ? 'EXISTING_NUMBER_SWAP'
          : 'SALE_NEW_NUMBER',
      };

      dispatch(
        orderActions.setFormData({
          ...values,
          selectedNumber:
            numberDetail?.data?.prefix + numberDetail?.data?.msisdn,
        }),
      );
      dispatch(
        orderActions.setInterestedNumberPrefix(numberDetail?.data?.prefix),
      );

      try {
        const response = await checkMutation(checkUserData).unwrap();
        dispatch(orderActions.setTrackId(response?.data?.trackId));
        setTrackId(response?.data?.trackId);
        localStorage.setItem('esimTID', response?.data?.trackId);
        if (response?.data?.customerExist) {
          setIsModalActive(true);
        } else setRecognationInfo(!recognationInfo);
      } catch (err) {
        if (err?.code === 142) {
          setErrorText(t('esim:common.reserved'));
          setReserved(!reserved);
          setError(!isError);
        }
        if (err?.code === 207) {
          setNotCustomer(!notCustomer);
        }
        if (err?.code === 205) {
          setOtpAttempt(!otpAttempt);
        }

        if (err?.code === 151) {
          setErrorText(t('esim:common.userNotFound'));
          setError(true);
        }
        if (err?.code === 400) {
          setErrorText(t('esim:common.userNotFound'));
          setError(true);
        }
      } finally {
        dispatch(orderActions.setDisabled(!disabled));
      }
    },
  });
  React.useEffect(() => {
    formikMain.validateForm();
  }, [lang]);
  const formikPhone = useFormik({
    initialValues: {
      error: false,
      confirmedPhone: false,
      code: ['', '', '', '', '', ''],
    },
    validationSchema: FormSchemaForOTP,
    async onSubmit(values, { setFieldValue }) {
      setExpired(false);

      const verifyUserData = {
        mobileNumber:
          formikMain?.values?.phoneNumberCode?.name +
          formikMain?.values?.phoneNumber,
        otp: values?.code?.join(''),
        trackId,
      };

      await verifyMutation(verifyUserData)
        .unwrap()
        .then(() => {
          setIsModalActive(false);
          setActiveTab(1);
        })
        .catch((err) => {
          if (err?.code === 151) {
            setErrorText(t('esim:common.errorTitle'));
            setError(!isError);
          }
          if (err?.code === 204) {
            setExpired(true);
            setTimer(null);
            setFinish(true);
          }
          if (err?.code === 205) {
            setOtpAttempt(!otpAttempt);
            setIsModalActive(false);
          }
          if (err?.code === 206) {
            formikPhone.resetForm();

            setWrongOtp(!wrongOtp);
            setFinish(true);
            setTimer(null);
          }
        });

      if (isSwapFlow) {
        swapMutation({
          trackId,
        })
          .unwrap()
          .then((res) => {
            dispatch(orderActions.setQrData(res?.data));
            setActiveTab(1);
            setIsModalActive(false);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'purchase',
            });
          })
          .catch((error) => {
            if (error?.data?.code === 156) {
              setSwapError(true);
              setWrongOtp(true);
            }
            if (error?.data?.code === 154) {
              setWrongOtp(true);
            } else setSalesError(true);
          });
      }

      setFieldValue('confirmedPhone', true);
    },
  });

  const goToNextStep = async () => {
    dispatch(orderActions.setDisabled(true));
    formikPhone.resetForm();
    setTimer(Date.now() + interval);

    switch (activeTab) {
      case 0:
        formikMain.handleSubmit();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'nextstep',
        });

        break;
      case 1:
        try {
          const { url } = await paymentTrigger({
            amount: totalAmount(),
            redirectUrl: RedirectUrls.ESIM_PAYMENT_REDIRECT,
            receiver: `994${formikMain.values.phoneNumberCode.name.replace(
              /^0+/,
              '',
            )}${formikMain.values.phoneNumber}`,
          }).unwrap();
          router.replace(url);
        } catch (error) {
          // console.log({ error });
        }
        break;
      default:
        setActiveTab(0);
    }
  };

  const handleApprove = () => {
    formikPhone.resetForm();
    setTimer(Date.now() + interval);
    formikMain.handleSubmit();
    setFinish(false);
    setWrongOtp(false);
    setExpired(false);
  };

  const stateHandlers = {
    setDeviceModal,
    setNotInListModal,
    setPinModal,
    onChange,
    setTermsModal,
  };

  if (isLoading || swapLoading || checkLoading) {
    return <Loader fullScreen={true} />;
  }

  const handleCheck = async (image) => {
    const formData = new FormData();
    formData.append('imageFile', image);

    try {
      const res = await simaMutation({ trackId, formData }).unwrap();
      if (res?.code === 200) {
        dispatch(orderActions.setActiveSima(false));
        setModalProps({
          isOpen: true,
          type: EModalType.SUCCESS,
        });
      }
    } catch (err) {
      dispatch(orderActions.setActiveSima(false));
      setModalProps({
        isOpen: true,
        type: EModalType.ERROR,
      });
      if (err?.code === 153) {
        setErrorTextSima(t('esim:common.expiredTransaction'));
      }
    }
  };

  const swapHandler = () => {
    setAccept((accept) => !accept);
    sessionStorage.setItem('accept', accept);
  };

  return (
    <div
      className={'wrapper checkout'}
      itemScope
      itemType='https://schema.org/Order'
    >
      {isModalActive && (
        <OtpEsim
          modalProps={{
            active: isModalActive,
            setActive: setIsModalActive,
          }}
          formikProps={{
            formikMain,
            formikPhone,
          }}
          otpProps={{
            timer,
            finish,
            setFinish,
            wrongOtp,
            expired,
          }}
          handleApprove={handleApprove}
        />
      )}
      <GoBack>{t('esim:checkout.previousPage')}</GoBack>
      <div className={styles.content}>
        <div className={styles.left}>
          <SectionTitle titleModify={'title--medium'} className={styles.title}>
            {isSwapFlow ? t('esim:common.swap') : t('esim:checkout.title')}
          </SectionTitle>
          <Tabs active={activeTab}>
            <TabPanel>
              {(isSwapFlow ? swapStepSales : salesStepLabels)?.map(
                (item, index) => {
                  return (
                    <Tab
                      key={index}
                      className={classNames([
                        styles.tab,
                        {
                          [styles.done]:
                            activeTab === ++index || activeTab > index,
                        },
                      ])}
                    >
                      <span className={styles.tabIndex}>{index}</span>
                      {t(`esim:checkout.steps.${item}`)}
                    </Tab>
                  );
                },
              )}
            </TabPanel>
            <TabContent>
              <TabContentItem
                className={classNames({
                  [styles.tabContentItem]: activeTab === 0,
                })}
              >
                <PersonalInfo
                  formik={formikMain}
                  stateHandlers={stateHandlers}
                />
                {isSwapFlow && (
                  <div className={styles.banner}>
                    <div>
                      <InfoCircleRed />
                    </div>
                    <span
                      className={styles.bannerText}
                      dangerouslySetInnerHTML={{
                        __html: isSwapFlow
                          ? t('esim:checkout.modals.swapInformation.info') +
                            t(
                              'esim:checkout.modals.swapInformation.additionalInfo',
                            )
                          : t('esim:checkout.modals.swapInformation.info'),
                      }}
                    ></span>
                  </div>
                )}
              </TabContentItem>
              <TabContentItem>
                {isSwapFlow && cardsData && (
                  <CardBlock
                    className={styles.cards}
                    linkClassName={true}
                    titleClassName={styles.cardsTitle}
                    data={cardsData}
                    gridValue='2'
                    titleIcon={null}
                    icon={'/assets/bkc.png'}
                  />
                )}
                {tariffData && !isSwapFlow && <EsimTariffs data={tariffData} />}
              </TabContentItem>
              <TabContentItem>
                {cardsData && (
                  <CardBlock
                    className={styles.cards}
                    titleClassName={styles.cardsTitle}
                    data={cardsData}
                    gridValue='2'
                    titleIcon={null}
                    icon={'/assets/bkc.png'}
                  />
                )}
              </TabContentItem>
            </TabContent>
          </Tabs>
        </div>
        <InfoCard
          formikMain={formikMain}
          activeTab={activeTab}
          numberDetail={numberDetail}
          goToNextStep={goToNextStep}
        />
      </div>
      {(activeTab === 2 || (activeTab === 1 && isSwapFlow)) &&
        result?.length > 0 && (
          <StepsBannerWithTabsVolte
            className='section wrapper'
            data={result[0]}
          />
        )}

      {termsModal && (
        <Terms
          setActive={setTermsModal}
          formik={formikMain}
          active={termsModal}
          data={contractData}
        />
      )}
      {notInListModal && (
        <NotInList
          active={notInListModal}
          setActive={() => setNotInListModal(!notInListModal)}
        />
      )}
      {pinModal && (
        <PinModal active={pinModal} setActive={() => setPinModal(!pinModal)} />
      )}
      {deviceData?.data && deviceModal && (
        <CompatibleDevicesModalEsim
          active={deviceModal}
          setActive={setDeviceModal}
          devices={fullDevices}
          choose
          getItem={getDeviceItem}
        />
      )}
      {isError && (
        <ErrorModal
          icon='/assets/error.png'
          active={isError}
          setActive={() => {
            setError(!isError);
            if (reserved) {
              router.replace('/esim');
              setReserved(!reserved);
            }
          }}
        >
          {errorText}
        </ErrorModal>
      )}
      {recognationInfo && (
        <RecognationInfoModal
          active={recognationInfo}
          setActive={() => {
            setRecognationInfo(!recognationInfo);
            dispatch(orderActions.setDisabled(!disabled));
          }}
          handleNext={() => {
            dispatch(orderActions.setActiveSima(true));
            setRecognationInfo(!recognationInfo);
          }}
        />
      )}
      <Sima
        active={activeSima}
        setActiveTab={() => {
          setActiveTab(1);
        }}
        handleCheck={handleCheck}
        isLoading={simaLoading}
        errorText={errorTextSima}
        modalProps={modalProps}
        closeModal={closeModal}
        path={'/esim'}
      />
      {accept && <SwapInformation active={accept} setActive={swapHandler} />}
      {swapError && (
        <SwapError
          active={swapError}
          setActive={() => setSwapError(!swapError)}
        />
      )}
      {otpAttempt && (
        <OtpError
          active={otpAttempt}
          setActive={() => setOtpAttempt(!otpAttempt)}
        />
      )}
      {salesError && (
        <SalesError
          active={salesError}
          setActive={() => setSalesError(!salesError)}
        />
      )}
      {notCustomer && (
        <NotCustomer
          active={notCustomer}
          setActive={() => {
            setNotCustomer(!notCustomer);
          }}
        />
      )}
    </div>
  );
};

export default Checkout;
